import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useReducedMotion } from 'framer-motion';
import { HomeTitle } from './HomeSectionHead';
import HomeDarkIsland from './HomeDarkIsland';
import { PiPause, PiPlay } from 'react-icons/pi';
import SceneScreen, { sceneMs, TICK_MS } from './HowScenes';
import { SELLER_FEE_PCT } from '../../utils/fees';

// Each step plays its scenes through to the end of their last animation,
// then holds that finished frame for HOLD_MS before advancing (2026-10-19,
// the user's call: "the video plays and completes, then waits two seconds").
// Every step follows the same rule, so steps are not all the same length.
const HOLD_MS = 2000;

// "How it works": a step list on the left, a scripted product screen on the
// right. The screen plays the active step on its own and the list advances
// when the scene ends, looping through the track. The scenes follow the real
// flow: the order request sheet over a listing, then the order page (sign,
// pay into escrow, delivered files, approve). Nothing goes through the inbox.
// One clock drives it all (the scene, the progress bar and the hold), so
// they can't drift apart. It only stops for the Pause button: it used to
// pause on hover, which froze the section for anyone whose mouse rested on
// it, i.e. everyone who had just clicked a step. Reduced-motion users get a
// still final frame and step through by clicking. The scenes are in HowScenes.jsx; their people, prices and
// files are illustrative samples.

/* ─── Tracks ───────────────────────────────────────────────────────────────── */

// `scene` is an id from SCENES in HowScenes.jsx.
// Exported for HomeMobile.jsx, which shows the same steps without the scenes.
export const TRACKS = {
  hire: {
    label: 'Hire a freelancer',
    folio: 'Buy a ready-made service',
    ctaLabel: 'Browse services',
    ctaTo: '/hire-talent',
    trustLine: 'Every freelancer is a current student or graduate.',
    steps: [
      {
        title: 'Browse freelancer services',
        blurb: 'Search ready-made services from verified students and alumni: portfolios, ratings, and prices up front.',
        scene: 'search',
      },
      {
        title: 'Request to order',
        blurb: 'Pick a package, write a short brief, and sign your request, all in one sheet. Nothing is charged yet: $0.00 is due today.',
        scene: 'request',
      },
      {
        title: 'They sign, you pay into escrow',
        blurb: 'The freelancer reviews your request and signs to accept it, which opens payment on your order page. What you pay is held in escrow, not paid out, until you approve the work.',
        scene: 'orderPay',
      },
      {
        title: 'Receive the work',
        blurb: 'The finished files land on your order page while your payment is still held. Look everything over, and request a revision if something is off.',
        scene: 'deliver',
      },
      {
        title: 'Approve and release payment',
        blurb: 'Approve the work and the payment releases to your freelancer. Then leave a review that helps the next buyer.',
        scene: 'release',
      },
    ],
  },
  postjob: {
    label: 'Post a job',
    folio: 'For custom work',
    ctaLabel: 'Post a job',
    ctaTo: '/post-a-job',
    trustLine: 'Proposals only from verified students and alumni.',
    steps: [
      {
        title: 'Post a job',
        blurb: 'Describe the work, pick a category, and set your budget: fixed, hourly, or negotiable. Post it publicly or limit it to chosen universities.',
        scene: 'postJob',
      },
      {
        title: 'Compare applicants side by side',
        blurb: 'Every applicant is a verified student or graduate. Their quotes, delivery times, and ratings sit side by side, with the strongest in each row marked.',
        scene: 'compare',
      },
      {
        title: 'Send an offer',
        blurb: 'Describe the work, pick a start date, and sign. Sending an offer doesn’t charge you anything.',
        scene: 'offer',
      },
      {
        title: 'They sign, you fund it',
        blurb: 'Your freelancer accepts and signs your offer on the contract page. You fund the contract, and the payment is held in escrow until you approve the work.',
        scene: 'contractFund',
      },
      {
        title: 'Approve and release payment',
        // Not every job produces a file to hand back (hourly and ongoing
        // roles don't), so the copy stays about approving the work; the
        // scene shows a fixed-price job that did.
        blurb: 'When they mark it complete, the work is on your contract page. Approve it and the payment releases, or ask for a revision first.',
        scene: 'contractDone',
      },
    ],
  },
  sell: {
    label: 'Start selling',
    folio: 'Turn your skills into income',
    ctaLabel: 'Post your first service',
    ctaTo: '/find-work',
    trustLine: `No listing fees. You set the price and keep ${100 - SELLER_FEE_PCT}% of every sale.`,
    steps: [
      {
        title: 'Publish your service',
        blurb: 'Set up to three packages at your own prices and watch your listing build itself as you go. Listing is free, and nothing goes live until you publish.',
        scene: 'listing',
      },
      {
        title: 'Requests come to you',
        blurb: 'Buyers find your service and send a signed request with their brief. It lands on your selling dashboard, marked as your move.',
        scene: 'requests',
      },
      {
        title: 'Sign to accept',
        blurb: 'Open the order, read the brief, and sign to accept it. The buyer then pays into escrow, so the money is there before you start.',
        scene: 'sellerOrder',
      },
      {
        title: 'Deliver the work',
        blurb: 'Upload the finished files on the order page. The buyer’s payment stays held while they review.',
        scene: 'upload',
      },
      {
        title: 'Get paid',
        blurb: 'When they approve, the payment releases straight to you and your earnings go up. Their review lands on your profile for the next buyer.',
        scene: 'payout',
      },
    ],
  },
};

const TRACK_KEYS = Object.keys(TRACKS);

/* ─── Section ──────────────────────────────────────────────────────────────── */

export default function HomeHowItWorks() {
  const reduceMotion = useReducedMotion();
  const [track, setTrack] = useState('hire');
  const [step, setStep] = useState(0);
  const [t, setT] = useState(0); // ms into the current step
  const [runKey, setRunKey] = useState(0); // restarts the scene + progress bar on a manual jump
  const [visible, setVisible] = useState(false);
  const [paused, setPaused] = useState(false);
  const sectionRef = useRef(null);

  const active = TRACKS[track];
  const current = active.steps[step];
  const playMs = sceneMs(current.scene);
  const totalMs = playMs + HOLD_MS;

  // Picking a step or a track plays it from the start, even if paused.
  function goTo(nextStep, nextTrack = track) {
    setTrack(nextTrack);
    setStep(nextStep);
    setT(0);
    setPaused(false);
    setRunKey((k) => k + 1);
  }

  // Only run the screen while the section is on screen, and restart the
  // WHOLE track from its first step every time it genuinely re-enters view —
  // not wherever it happened to be. Without this, a user who scrolls past
  // the section quickly (letting it run unseen for a few seconds) and then
  // scrolls back up to actually read it lands mid-track instead of at
  // "Browse freelancer services," which reads as "it already started
  // without me." `wasVisible` tracks the last real (debounced) visibility
  // state so this only fires on a genuine leave-then-return, not on every
  // render while already visible.
  //
  // Leaving view is debounced: the hero above this section is still settling
  // its own layout (fonts, canvas sizing) for a moment after first load, which
  // can nudge this section's intersection ratio across the 0.25 threshold and
  // back within a frame or two. Without the debounce that flicker unmounts and
  // remounts the scene on every crossing, which looked like the first step
  // "taking forever" to advance — it was actually restarting from zero each
  // time. Becoming visible still applies immediately.
  const wasVisible = useRef(false);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }
    let hideTimer;
    const obs = new IntersectionObserver(([e]) => {
      clearTimeout(hideTimer);
      if (e.isIntersecting) {
        if (!wasVisible.current) {
          setStep(0);
          setT(0);
          setRunKey((k) => k + 1);
        }
        wasVisible.current = true;
        setVisible(true);
      } else {
        hideTimer = setTimeout(() => { wasVisible.current = false; setVisible(false); }, 200);
      }
    }, { threshold: 0.25 });
    obs.observe(el);
    return () => { clearTimeout(hideTimer); obs.disconnect(); };
  }, []);

  // The clock. It runs while the section is on screen and not paused; each
  // tick is capped so a throttled background tab can't jump a whole scene.
  const running = visible && !paused && !reduceMotion;
  useEffect(() => {
    if (!running) return undefined;
    let last = performance.now();
    const id = setInterval(() => {
      // Measured out here, not in the updater: React can run an updater
      // later, after `last` has moved on, and the tick would count as 0.
      const now = performance.now();
      const dt = Math.min(now - last, 250);
      last = now;
      setT((v) => v + dt);
    }, TICK_MS);
    return () => clearInterval(id);
  }, [running]);

  // The scene has played and its finished frame has been up for HOLD_MS:
  // on to the next step, wrapping so the track loops.
  useEffect(() => {
    if (t < totalMs) return;
    setStep((s) => (s + 1) % active.steps.length);
    setT(0);
    setRunKey((k) => k + 1);
  }, [t, totalMs, active.steps.length]);

  // ─── Sliding thumb — measured, not assumed-equal ──────────────────────────
  // The three labels aren't the same length, so the buttons (flex-1 +
  // whitespace-nowrap) don't render as equal thirds. Measuring each button's
  // real offsetLeft/offsetWidth keeps the pill exactly under the active label
  // at any viewport width.
  const tabRefs = useRef({});
  const [thumbRect, setThumbRect] = useState(null);
  useLayoutEffect(() => {
    function measure() {
      const el = tabRefs.current[track];
      if (el) setThumbRect({ left: el.offsetLeft, width: el.offsetWidth });
    }
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [track]);

  return (
    <section ref={sectionRef} className="px-3 pb-16 sm:px-6 lg:px-10" aria-label="How it works">
      <HomeDarkIsland night className="hw-island">
      <div className="flex flex-wrap items-end justify-between gap-5">
        <div className="max-w-[46ch]">
          <HomeTitle title="How it works" />
          <p className="mt-2.5 text-[14px] leading-[1.6] text-press-muted">
            Every step happens directly between verified university students and alumni, at prices you set together.
          </p>
        </div>

        {/* max-w-full + overflow-x-auto is the safety net: the labels don't all
            fit a ~320-375px phone, so this degrades to a swipeable strip
            instead of forcing the page into horizontal scroll. */}
        <div className="flex max-w-full items-center gap-2">
        <div
          role="tablist"
          aria-label="How it works, by role"
          className="relative flex max-w-full flex-none overflow-x-auto rounded-full border border-press-border bg-press-card p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          <span
            aria-hidden="true"
            className="absolute inset-y-1 rounded-full bg-press-ink transition-[left,width] duration-300 ease-out"
            style={
              thumbRect
                ? { left: `${thumbRect.left}px`, width: `${thumbRect.width}px` }
                : { left: '4px', width: `calc((100% - 8px) / ${TRACK_KEYS.length})` }
            }
          />
          {TRACK_KEYS.map((key) => (
            <button
              key={key}
              ref={(el) => { tabRefs.current[key] = el; }}
              type="button"
              role="tab"
              aria-selected={track === key}
              onClick={() => key !== track && goTo(0, key)}
              className={`relative z-10 flex-1 whitespace-nowrap rounded-full px-3 py-2.5 text-center text-[12px] font-semibold transition-colors sm:px-5 sm:text-[13px] ${
                track === key ? 'text-press-paper' : 'text-press-muted hover:text-press-ink'
              }`}
            >
              {TRACKS[key].label}
            </button>
          ))}
        </div>
        {!reduceMotion && (
          <button
            type="button"
            onClick={() => setPaused((v) => !v)}
            aria-label={paused ? 'Play the walkthrough' : 'Pause the walkthrough'}
            className="grid h-11 w-11 flex-none place-items-center rounded-full border border-press-border bg-press-card text-press-ink transition hover:bg-press-paper-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-press-ink"
          >
            {paused ? <PiPlay size={16} aria-hidden="true" /> : <PiPause size={16} aria-hidden="true" />}
          </button>
        )}
        </div>
      </div>

      <div className="mt-10 grid items-start gap-8 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-14">
        {/* Step list: each step is its own framed panel (see .hw-fstep) —
            the active one gets a brass border, a faint tint fill, and a lift
            shadow; everything else stays a quiet single-line row. Below lg
            the screen sits above it so the visual stays next to the tabs
            instead of sliding below five rows of text. */}
        <ol className="order-2 flex flex-col gap-2.5 lg:order-1 lg:mt-2">
          {active.steps.map((s, i) => {
            const on = i === step;
            return (
              <li key={`${track}-${s.title}`} className={`hw-fstep${on ? ' is-on' : ''}`}>
                <button
                  type="button"
                  onClick={() => goTo(i)}
                  aria-current={on ? 'step' : undefined}
                  className="flex w-full items-start text-left hover:opacity-80"
                >
                  <span
                    className={`font-press-display leading-[1.25] transition-[font-size,color] duration-300 ${
                      on ? 'text-[1.5rem] text-press-ink' : 'text-[1.2rem] text-press-muted hover:text-press-ink'
                    }`}
                  >
                    {s.title}
                  </span>
                </button>
                <div className="hw-blurb" aria-hidden={!on}>
                  <div>
                    <p className="max-w-[46ch] text-[14px] leading-[1.6] text-press-muted">{s.blurb}</p>
                    {on && !reduceMotion && (
                      <div className="hw-bar">
                        <i style={{ transform: `scaleX(${Math.min(1, t / totalMs)})` }} />
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>

        <div className="order-1 lg:order-2">
          {visible ? (
            <SceneScreen
              key={`${track}-${step}-${runKey}`}
              id={current.scene}
              instant={!!reduceMotion}
              t={Math.min(t, playMs)}
            />
          ) : (
            <div className="hw-screen" aria-hidden="true">
              <div className="hw-screen-h">&nbsp;</div>
              <div className="hw-body" />
            </div>
          )}
        </div>
      </div>

      <div className="mt-10 flex flex-col items-center gap-4 text-center">
        <p className="text-[13px] text-press-muted">{active.trustLine}</p>
        <Link
          to={active.ctaTo}
          className="inline-flex items-center gap-2 rounded-full bg-press-ink px-7 py-3 text-sm font-semibold text-press-paper transition hover:bg-press-ink/90"
        >
          {active.ctaLabel}
        </Link>
      </div>
      </HomeDarkIsland>
    </section>
  );
}
